import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { applyBrandColor } from "@/lib/color";
import { backendAssetBase } from "../lib/assets";
import { setFavicon } from "../lib/favicon";
import { installRequestLoader } from "../lib/requestLoader";
import { useLoaderBranding } from "./LoaderBrandingContext";
import useLiveBranding from "../hooks/useLiveBranding";
import { PLATFORM_BRANDING_SCOPE } from "../lib/brandingChanges";

installRequestLoader(axios);

const API = `${backendAssetBase()}/api/master`;
const FALLBACK = { platform_name: "", platform_tagline: "Master Portal", primary_color: "#22D3EE" };

const Ctx = createContext({ brand: FALLBACK, refresh: () => {} });

export const useBranding = () => useContext(Ctx);

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(FALLBACK);
  useLoaderBranding({ scope: "master", name: brand.platform_name || FALLBACK.platform_name,
    logoUrl: brand.platform_logo_url || "", merchantCode: "" });

  const load = useCallback(async signal => (await axios.get(`${API}/public/branding`, { signal, globalLoader: false, timeout: 10000 })).data, []);
  const refresh = useLiveBranding(PLATFORM_BRANDING_SCOPE, load, data => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return;
    const next = { ...FALLBACK, ...data };
    setBrand(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
  }, { initial: true });

  useEffect(() => {
    const name = brand.platform_name || FALLBACK.platform_name;
    if (brand.primary_color) applyBrandColor(brand.primary_color);
    document.title = brand.meta_title || `${name} — ${brand.platform_tagline || "Platform Console"}`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description";
      document.head.appendChild(m); return m;
    })();
    if (brand.meta_description) meta.content = brand.meta_description;
    setFavicon(brand.platform_favicon_url);
  }, [brand]);

  return <Ctx.Provider value={{ brand, refresh }}>{children}</Ctx.Provider>;
}

export { assetUrl } from "../lib/assets";
